import React, { memo, useCallback, useMemo, useState } from 'react'

import { Icon, MoreChorizontal, RotatingSpinner, classnames } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { arrayOf, bool, func, number, oneOfType, shape, string } from 'prop-types'

import { useLocation, useNavigate } from 'Pods/Shared/ContextBasedRouter'
import { STUDIO_PATH, buildImagePath } from 'Pods/ThreeDStudio/constants'
import useApplyImageToThreeD from 'Pods/ThreeDStudio/Hooks/useApplyImageToThreeD'
import useDeleteImageRun from 'Pods/ThreeDStudio/Hooks/useDeleteImageRun'
import usePrivateModeAccess from 'Pods/ThreeDStudio/Hooks/usePrivateModeAccess'
import useRegenerateImageRun from 'Pods/ThreeDStudio/Hooks/useRegenerateImageRun'
import useStudioAccessTierPending from 'Pods/ThreeDStudio/Hooks/useStudioAccessTierPending'
import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'
import useUpdateRunPrivacy from 'Pods/ThreeDStudio/Hooks/useUpdateRunPrivacy'
import DeleteConfirmModal from 'Pods/ThreeDStudio/Shared/DeleteConfirmModal/DeleteConfirmModal'
import Dropdown from 'Pods/ThreeDStudio/Shared/Dropdown/Dropdown'
import ImageLightbox from 'Pods/ThreeDStudio/Shared/ImageLightbox/ImageLightbox'
import { getAssetByTypeAndSource } from 'Pods/ThreeDStudio/Shared/Utils/getAssetByTypeAndSource'
import { openFullImageEditor } from 'Pods/ThreeDStudio/Store/actions/ui'

import ImageCardContent from './Components/ImageCardContent/ImageCardContent'

const size = { height: '36px', width: '36px' }
const spinnerSize = { height: '24px', width: '24px' }
const icon = (
  <Icon notSpaced svgComponent={MoreChorizontal} color="dark" size={size} className="model-card__icon-dropdown" />
)

// Matches ModelCard: the menu trigger wrapper sits at z-index 452, so the portal'd menu
// has to clear it for tooltips overflowing the menu.
const PORTAL_Z_INDEX = 453

// One generated image in My Assets > Images. Shares the .model-card block with ModelCard
// because Figma draws both from the same `Model preview` component; only the menu and the
// delete target differ (a run here, a whole session there).
function ImageCard({ runId, attributes, onNavigate, isProcessing = false }) {
  const t = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [shouldCloseDropdown, setShouldCloseDropdown] = useState(false)
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isLightboxOpen, setIsLightboxOpen] = useState(false)

  const { outputAssets, title, prompt, createdAt, private: isPrivateAttribute, slug } = attributes || {}
  const imageTitle = title ?? prompt ?? ''
  const {
    previewUrl: outputPreviewUrl,
    fileUrl: outputFileUrl,
    assetId: outputAssetId,
  } = getAssetByTypeAndSource(outputAssets, 'image', 'ai_generated')
  // The image providers store the generated image as the asset file and attach no preview,
  // so the file itself is the fallback thumbnail.
  const previewUrl = outputPreviewUrl ?? outputFileUrl

  // Addressed by the asset id and the full file, not the thumbnail: that is what the
  // editor starts its runs against.
  const lightboxImage = useMemo(
    () => (outputAssetId && outputFileUrl ? { id: outputAssetId, url: outputFileUrl, alt: imageTitle } : null),
    [outputAssetId, outputFileUrl, imageTitle],
  )

  const handleOpenDelete = useCallback(() => {
    setIsDeleteModalOpen(true)
    setShouldCloseDropdown(true)
  }, [])

  const handleCloseDelete = useCallback(() => {
    setIsDeleteModalOpen(false)
  }, [])

  const { deleteImage, isLoading: isDeleting } = useDeleteImageRun({ onSuccess: handleCloseDelete })

  // A card with a slug opens its shareable /ai-image/:slug URL, where ImageDeepLink shows
  // the lightbox. A run without a slug can only open it in place.
  const handleOpenLightbox = useCallback(() => {
    setShouldCloseDropdown(true)
    if (slug) {
      navigate(buildImagePath(slug))
      onNavigate('image')
      return
    }
    setIsLightboxOpen(true)
  }, [slug, navigate, onNavigate])

  const handleCloseLightbox = useCallback(() => {
    setIsLightboxOpen(false)
  }, [])

  // No session id: the library row does not carry one, so the editor's first edit starts
  // its own session. canReturnToCreate stays off, since the library is not the Create page.
  const handleEditImage = useCallback(() => {
    openFullImageEditor({ image: lightboxImage, runId })
  }, [lightboxImage, runId])

  const canOpenLightbox = Boolean(lightboxImage) && !isProcessing

  // A card with a slug opens its shareable /ai-image/:slug?edit=true URL, where ImageDeepLink
  // opens the editor. Without one, the editor opens in place, and since it only renders on
  // routes that host it, not on the Community galleries, that lands on the Image Generator.
  const handleMenuEditImage = useCallback(() => {
    setShouldCloseDropdown(true)
    if (slug) {
      navigate(buildImagePath(slug, { edit: true }))
      onNavigate('image')
      return
    }
    handleEditImage()
    if (location?.pathname !== STUDIO_PATH.IMAGE_GENERATOR) {
      navigate(STUDIO_PATH.IMAGE_GENERATOR, { state: { keepImageEditor: true } })
    }
    onNavigate('image')
  }, [slug, handleEditImage, location, navigate, onNavigate])

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      handleOpenLightbox()
    }
  }, [handleOpenLightbox])

  const applyImageToThreeD = useApplyImageToThreeD(onNavigate)

  const handleApplyImage = useCallback(() => {
    applyImageToThreeD(lightboxImage)
  }, [applyImageToThreeD, lightboxImage])

  const handleMenuApplyImage = useCallback(() => {
    setShouldCloseDropdown(true)
    handleApplyImage()
  }, [handleApplyImage])

  const { regenerateImage, isLoading: isRegenerating } = useRegenerateImageRun()

  const handleRegenerateImage = useCallback(() => {
    regenerateImage(runId)
  }, [regenerateImage, runId])

  const handleConfirmDelete = useCallback(() => {
    deleteImage(runId)
  }, [deleteImage, runId])

  // Same reasoning as ModelCard: the library is client-only, so gating on the
  // login-pending window is safe, and a logged-in user is treated as entitled while the
  // tier resolves so the paid-only crown never flashes for subscribers.
  const hasPrivateModeAccess = usePrivateModeAccess()
  const isTierPending = useStudioAccessTierPending()
  const canTogglePrivacy = hasPrivateModeAccess || isTierPending
  const { updateRunPrivacy, isLoading: isPrivacyUpdating } = useUpdateRunPrivacy()
  // useUpdateRunPrivacy reflects the new value by patching the *sessions* cache, which
  // holds no row for an image run, so the toggle is tracked here instead and the server
  // value takes over on the next fetch.
  const [isPrivate, setIsPrivate] = useState(Boolean(isPrivateAttribute))

  const handleTogglePrivacy = useCallback(() => {
    setIsPrivate((wasPrivate) => !wasPrivate)
    updateRunPrivacy({ runId, isPrivate: !isPrivate })
  }, [updateRunPrivacy, runId, isPrivate])

  // runs#destroy rejects a run that is still in flight, so an in-flight card's menu stays
  // read-only until the run settles.
  const content = useMemo(() =>
    <ImageCardContent
      title={imageTitle}
      createdAt={createdAt}
      runId={runId}
      onDelete={handleOpenDelete}
      onEdit={lightboxImage ? handleMenuEditImage : undefined}
      onUseForThreeD={lightboxImage ? handleMenuApplyImage : undefined}
      isDeleting={isDeleting}
      isPrivate={isPrivate}
      canTogglePrivacy={canTogglePrivacy}
      onTogglePrivacy={handleTogglePrivacy}
      isPrivacyUpdating={isPrivacyUpdating}
      isReadOnly={isProcessing}
    />,
    [
      imageTitle,
      createdAt,
      runId,
      handleOpenDelete,
      lightboxImage,
      handleMenuEditImage,
      handleMenuApplyImage,
      isDeleting,
      isPrivate,
      canTogglePrivacy,
      handleTogglePrivacy,
      isPrivacyUpdating,
      isProcessing,
    ],
  )

  return (
    <>
      <div
        className={classnames(
          'model-card',
          !canOpenLightbox && 'model-card--inert',
          isMenuOpen && 'model-card--menu-open',
          isProcessing && 'model-card--processing',
        )}
        {...(canOpenLightbox && {
          onClick: handleOpenLightbox,
          onKeyDown: handleKeyDown,
          role: 'button',
          tabIndex: 0,
        })}
      >
        {previewUrl && (
          <img
            className={classnames('model-card__image', isProcessing && 'model-card__image--processing')}
            src={previewUrl}
            alt={imageTitle}
          />
        )}
        {isProcessing && (
          <div className="model-card__processing">
            <Icon svgComponent={RotatingSpinner} size={spinnerSize} color="primary" notSpaced />
            <span className="model-card__processing-text">{t('sidebar.status.processing')}</span>
          </div>
        )}
        <Dropdown
          icon={icon}
          content={content}
          placement="bottom-start"
          className="model-card__dropdown-trigger"
          contentClassName="model-card__dropdown-content"
          triggerCloseDropdown={shouldCloseDropdown}
          setTriggerCloseDropdown={setShouldCloseDropdown}
          onOpenChange={setIsMenuOpen}
          portalZIndex={PORTAL_Z_INDEX}
          usePortal
        />
      </div>
      {isDeleteModalOpen && (
        <DeleteConfirmModal
          itemType="image"
          itemName={imageTitle}
          isSubmitDisabled={isDeleting}
          onSubmit={handleConfirmDelete}
          onCancel={handleCloseDelete}
        />
      )}
      {isLightboxOpen && lightboxImage && (
        <ImageLightbox
          result={lightboxImage}
          isGenerating={isRegenerating}
          onApply={handleApplyImage}
          onRegenerate={handleRegenerateImage}
          onEdit={handleEditImage}
          onClose={handleCloseLightbox}
        />
      )}
    </>
  )
}

ImageCard.propTypes = {
  runId: oneOfType([string, number]).isRequired,
  attributes: shape({
    outputAssets: arrayOf(shape({
      assetId: number,
      assetType: string,
      source: string,
      previewUrl: string,
      fileUrl: string,
    })),
    slug: string,
    title: string,
    prompt: string,
    createdAt: string.isRequired,
    private: bool,
  }).isRequired,
  onNavigate: func.isRequired,
  isProcessing: bool,
}

export default memo(ImageCard)
